import {Component, OnInit, ViewChild} from '@angular/core';
import {FormControl, FormGroup, UntypedFormControl} from "@angular/forms";
import {Subject, takeUntil} from "rxjs";
import {MatTableDataSource} from "@angular/material/table";
import {MatPaginator, PageEvent} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {TableColumn} from "@vex/interfaces/table-column.interface";
import {MatDialog} from "@angular/material/dialog";
import {TranslateService} from "@ngx-translate/core";
import {MatSnackBar} from "@angular/material/snack-bar";
import {ProjectTypesService} from "@core/services/backend/common/service/project-types.service";
import {fadeInUp400ms} from "@vex/animations/fade-in-up.animation";
import {stagger40ms} from "@vex/animations/stagger.animation";
import {ProjectType} from "@core/services/backend/common/interface/project-types";
import {ProjectTypesCreateUpdateComponent} from "@app/pages/administration/project-types-create-update/project-types-create-update.component";

@Component({
    selector: 'vex-project-types',
    templateUrl: './project-types.component.html',
    styleUrls: ['./project-types.component.scss'],
    animations: [
        fadeInUp400ms,
        stagger40ms
    ]
})
export class ProjectTypesComponent implements OnInit {

    layoutCtrl = new UntypedFormControl('boxed');
    private readonly destroying$ = new Subject<void>();

    projectTypesList: ProjectType[] = [];
    dataSource: MatTableDataSource<ProjectType> | null;
    searchForm: FormGroup;
    @ViewChild(MatPaginator, {static: true}) paginator: MatPaginator;
    @ViewChild(MatSort, {static: true}) sort: MatSort;

    columns: TableColumn<ProjectType>[] = [
        {label: 'GRIDS.ID', property: 'id', type: 'text', visible: false},
        {label: 'GRIDS.NOMBRE', property: 'name', type: 'text', visible: true, cssClasses: ['font-medium']},
        {label: 'GRIDS.ACTIVO', property: 'active', type: 'image', visible: true},
        {label: 'Lista niveles', property: 'mostrarListaNiveles', type: 'image', visible: true},
        {label: 'GRIDS.ACCIONES', property: 'actions', type: 'button', visible: true}
    ];

    currentPage = 0;
    pageSize = 20;
    pageSizeOptions: number[] = [10, 20, 50, 100];
    isLoading: boolean = false;

    constructor(
        private projectTypes: ProjectTypesService,
        private dialog: MatDialog,
        private translateService: TranslateService,
        private snackbar: MatSnackBar
    ) {
    }

    ngOnInit(): void {
        this.dataSource = new MatTableDataSource();
        this.searchForm = new FormGroup({
            name: new FormControl()
        });
        this.loadData();
    }

    loadData() {
        const that = this;
        this.isLoading = true;
        this.initializeData().then(() => {
            this.dataSource.data = that.projectTypesList;
            this.isLoading = false;
        });
    }

    private getSearch() {
        return {
            name: this.searchForm.get("name")?.value ? '%' + this.searchForm.get("name")?.value.toString().toUpperCase() + '%' : ''
        };
    }

    async initializeData() {
        const that = this;
        return await new Promise<void>((resolve, reject) => {
            const list = this.projectTypes.list(JSON.stringify(that.getSearch()), that.currentPage, that.pageSize, 'name ASC');
            list.pipe(takeUntil(this.destroying$)).subscribe({
                next: (data: any) => {
                    const items = data.data || data || [];
                    // Asegurar que los IDs estén correctamente mapeados
                    that.projectTypesList = items.map((item: any) => {
                        const id = item.id || item.ID;
                        return {
                            ...item,
                            id: id ? Number(id) : id
                        };
                    });
                    that.paginator.pageIndex = that.currentPage;
                    that.paginator.length = data.total_records || data.totalRecords || that.projectTypesList.length;
                    resolve();
                },
                error: (err) => {
                    console.error('Error loading project types:', err);
                    that.projectTypesList = [];
                    resolve();
                }
            });
        });
    }

    pageChanged(event: PageEvent) {
        this.pageSize = event.pageSize;
        this.currentPage = event.pageIndex;
        this.loadData();
    }

    trackByProperty<T>(index: number, column: TableColumn<T>) {
        return column.property;
    }

    get visibleColumns() {
        return this.columns.filter(column => column.visible).map(column => column.property);
    }

    buscar() {
        this.currentPage = 0;
        this.loadData();
    }

    createProjectType() {
        this.dialog.open(ProjectTypesCreateUpdateComponent, {
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    updateProjectType(projectType: ProjectType) {
        this.dialog.open(ProjectTypesCreateUpdateComponent, {
            data: projectType,
            maxWidth: '100vw',
            maxHeight: '100vh',
            width: '500px',
            panelClass: 'full-screen-modal'
        }).afterClosed().subscribe(() => {
            this.loadData();
        });
    }

    deleteProjectType(projectType: ProjectType) {
        const that = this;
        if (!projectType || (projectType.id === undefined || projectType.id === null)) {
            console.error('ProjectType sin ID:', projectType);
            that.snackbar.open('Error: ID del tipo de proyecto no válido', '', {
                duration: 3000
            });
            return;
        }
        this.projectTypes.delete(projectType).subscribe({
            next(result) {
                that.snackbar.open('Tipo de Proyecto eliminado correctamente', '', {
                    duration: 3000
                });
                that.loadData();
            },
            error(err) {
                console.error('Error al eliminar:', err);
                that.snackbar.open('Error al eliminar el tipo de proyecto', '', {
                    duration: 3000
                });
            }
        });
    }
}

